import { Theme } from '@mui/material'
import { useEffect, useRef } from 'react'
import { Dispatch } from 'redux'
import {
  addMarkers,
  fetchAggregatedWaterSystems,
  removeMarkers,
  updateBoundingBoxStyles,
  updateCountyStyles,
  updateRegionStyles,
} from '../../../components/MapSection/features/publicWaterSystem'
import { stateToRegionByName } from '../../../constants/Map'
import { IAddress } from '../../../store/Address'

import {
  MapWaterSystemsMetricsActions,
  WaterSystemCoordinatesActions,
  WaterSystemsMetricsActions,
  ZoomLevel,
} from '../../../store/publicWaterSystem'
import { BoundingBox } from '../../../types'
import { useDispatch } from 'react-redux'
import { MapActions } from '../../../store/Map'

interface MapRefs {
  regionsViolationsRef: React.MutableRefObject<any>
  countyViolationsRef: React.MutableRefObject<any>
  markersRef: React.MutableRefObject<any[]>
}

interface MapEffectsProps {
  boundingBox: BoundingBox | undefined
  coordinates: any[]
  countyViolations: any[]
  dispatch: Dispatch
  includeTransient?: boolean
  isMobile?: boolean
  mapCenter: any
  mapCenterRef: React.MutableRefObject<any>
  mapRef: React.RefObject<mapboxgl.Map>
  prevSelectedCode: React.MutableRefObject<any>
  refs: MapRefs
  regionsViolations: any[]
  selectedAddress: IAddress | null
  selectedAddressRef: React.MutableRefObject<any>
  selectedCounty: any
  selectedState: any
  selectedStateRef: React.MutableRefObject<any>
  states: any
  statesRef: React.MutableRefObject<any>
  theme: Theme
  waterSystemMapMetrics: any
  waterSystemMapMetricsRef: React.MutableRefObject<any>
  zoomLevel: string | ZoomLevel
  zoom: { zoom: number; ease: boolean; speed: number; curve: number }
}

// Hook for handling map zoom
const useMapZoom = (
  mapRef: React.RefObject<mapboxgl.Map>,
  zoom: { zoom: number; ease: boolean },
) => {
  useEffect(() => {
    if (!mapRef.current) return

    if (zoom.ease) {
      mapRef.current.zoomTo(zoom.zoom)
    } else {
      mapRef.current.setZoom(zoom.zoom)
    }
  }, [zoom, mapRef])
}

// Hook for handling water system metrics
const useWaterSystemMetrics = (
  dispatch: Dispatch,
  zoomLevel: string | ZoomLevel,
  waterSystemMapMetrics: any,
  waterSystemMapMetricsRef: React.MutableRefObject<any>,
  includeTransient?: boolean,
) => {
  useEffect(() => {
    if (zoomLevel === ZoomLevel.REGION) {
      fetchAggregatedWaterSystems(dispatch, zoomLevel as ZoomLevel, undefined, includeTransient)
      dispatch(MapWaterSystemsMetricsActions.fetchMapWaterSystemMetricsAsync({}) as any)
    }
  }, [zoomLevel, dispatch, includeTransient])

  useEffect(() => {
    waterSystemMapMetricsRef.current = waterSystemMapMetrics
  }, [waterSystemMapMetrics, waterSystemMapMetricsRef])
}

// Hook for re-fetching data when includeTransient changes
const useIncludeTransientRefetch = (
  dispatch: Dispatch,
  zoomLevel: string | ZoomLevel,
  selectedState: any,
  countyViolations: any[],
  selectedCounty: string | null,
  includeTransient?: boolean,
) => {
  const isFirstRender = useRef(true)
  const prevIncludeTransient = useRef(includeTransient)

  useEffect(() => {
    // Skip first render
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    // Only act if includeTransient actually changed
    if (prevIncludeTransient.current === includeTransient) {
      return
    }
    prevIncludeTransient.current = includeTransient

    // Re-fetch aggregates if we have a selected state at county level
    if (zoomLevel === ZoomLevel.COUNTY && selectedState?.abbreviation) {
      fetchAggregatedWaterSystems(dispatch, ZoomLevel.COUNTY, selectedState.abbreviation, includeTransient)
    }

    // Re-fetch coordinates if we have a selected county
    if (selectedCounty && countyViolations?.length) {
      const county = countyViolations.find(
        (item) => item?.county_name?.trim()?.toLowerCase() === selectedCounty?.trim()?.toLowerCase(),
      )
      if (county?.waterSystemIds) {
        dispatch(
          WaterSystemCoordinatesActions.createWaterSystemCoordinatesAsync({
            waterSystemIds: county.waterSystemIds,
            includeTransient,
          }) as any,
        )
      }
    }
  }, [includeTransient, dispatch, zoomLevel, selectedState, countyViolations, selectedCounty])
}

// Hook for handling map styles
const useMapStyles = (
  mapRef: React.RefObject<mapboxgl.Map>,
  zoomLevel: string | ZoomLevel,
  regionsViolations: any[],
  countyViolations: any[],
  refs: MapRefs,
  prevSelectedCode: React.MutableRefObject<any>,
  boundingBox: BoundingBox | undefined,
  selectedAddress: IAddress | null,
  dispatch: Dispatch,
) => {
  // Update region styles
  useEffect(() => {
    if (mapRef.current && zoomLevel === ZoomLevel.REGION) {
      if (!mapRef.current.isStyleLoaded()) {
        const onStyleLoaded = () => {
          updateRegionStyles({
            mapRef,
            regionsViolations,
            regionsViolationsRef: refs.regionsViolationsRef,
          })
          mapRef.current?.off('styledata', onStyleLoaded)
        }
        mapRef.current?.on('styledata', onStyleLoaded)
        return
      }
      updateRegionStyles({
        mapRef,
        regionsViolations,
        regionsViolationsRef: refs.regionsViolationsRef,
      })
    }
  }, [regionsViolations, mapRef, zoomLevel, refs.regionsViolationsRef])

  // Update county styles
  useEffect(() => {
    if (mapRef.current && countyViolations) {
      updateCountyStyles(
        mapRef,
        countyViolations,
        refs.countyViolationsRef,
        zoomLevel,
        prevSelectedCode,
      )
    }
  }, [countyViolations, mapRef, zoomLevel, refs.countyViolationsRef])

  // Update search radius styles
  useEffect(() => {
    if (mapRef.current && boundingBox) {
      updateBoundingBoxStyles(mapRef, boundingBox)
    }

    if (selectedAddress) {
      const { code } = stateToRegionByName[selectedAddress.region] || {}
      const options = {
        params: {
          state_abbr: code,
        },
      }

      dispatch(WaterSystemsMetricsActions.fetchWaterSystemMetricsAsync({ options }) as any)
    }
  }, [boundingBox, mapRef, zoomLevel, selectedAddress, dispatch])
}

// Hook for handling adding markers on map
const useMapMarkers = (
  mapRef: React.RefObject<mapboxgl.Map>,
  coordinates: any[],
  refs: MapRefs,
  theme: Theme,
  dispatch: Dispatch,
  selectedAddress: IAddress | null,
  isMobile: boolean,
) => {
  // Handle markers
  useEffect(() => {
    if (coordinates?.length) {
      addMarkers(mapRef, coordinates, refs.markersRef, theme, dispatch, selectedAddress, isMobile)
    }
    return () => {
      removeMarkers(refs.markersRef)
    }
  }, [coordinates, mapRef, refs.markersRef, theme, dispatch, isMobile])
}

// Hook for handling map resize
const useMapResize = (mapRef: React.RefObject<mapboxgl.Map>) => {
  const handleResize = () => mapRef.current?.resize()

  useEffect(() => {
    window.addEventListener('mapContainerResize', handleResize)
    return () => window.removeEventListener('mapContainerResize', handleResize)
  }, [mapRef])
}

// Hook for handling map movements and transitions based on selected state, county, and address
const useMapNavigationTranisitions = (
  mapRef: React.RefObject<mapboxgl.Map>,
  selectedState: any,
  selectedStateRef: React.MutableRefObject<any>,
  mapCenter: any,
  mapCenterRef: React.MutableRefObject<any>,
  selectedAddress: IAddress | null,
  selectedAddressRef: React.MutableRefObject<any>,
  states: any,
  statesRef: React.MutableRefObject<any>,
  selectedCounty: any,
  dispatch: Dispatch,
  zoom: { zoom: number; ease: boolean; speed: number; curve: number },
) => {
  // Handle selected state(dropdown) changes
  useEffect(() => {
    selectedStateRef.current = selectedState

    if (selectedState?.location) {
      const [lat, lng] = selectedState.location
      const zoom = selectedState.default_zoom

      // First zooms out to zoom level 4 to show context
      mapRef.current?.flyTo({
        center: [lng, lat],
        zoom: 4,
      })
      // Once the initial zoom out is complete, zooms in to the state's default zoom level
      mapRef.current?.once('moveend', () => {
        mapRef.current?.flyTo({
          center: [lng, lat],
          zoom,
        })
      })
    }
  }, [selectedState, dispatch, mapRef])

  useEffect(() => {
    statesRef.current = states
  }, [states])
  // Handle selected county changes and ensures the map is centered on the selected county pin and zoom level
  useEffect(() => {
    mapCenterRef.current = mapCenter
  }, [selectedCounty])

  // Handle selected address changes and ensures the map is centered on the selected address pin and zoom level
  useEffect(() => {
    if (!selectedAddress) {
      selectedAddressRef.current = null
      return
    }
    selectedAddressRef.current = selectedAddress
    const { latitude, longitude } = selectedAddress
    const zoomLevel = zoom.zoom
    const speed = zoom.speed
    const curve = zoom.curve

    mapRef.current?.flyTo({
      center: [longitude, latitude],
      zoom: zoomLevel,
      speed: speed,
      curve: curve,
    })
  }, [selectedAddress])
}

export const useMapEffects = ({
  boundingBox,
  coordinates,
  countyViolations,
  dispatch,
  includeTransient,
  isMobile = false,
  mapCenter,
  mapCenterRef,
  mapRef,
  prevSelectedCode,
  refs,
  regionsViolations,
  selectedAddress,
  selectedAddressRef,
  selectedCounty,
  selectedState,
  selectedStateRef,
  states,
  statesRef,
  theme,
  waterSystemMapMetrics,
  waterSystemMapMetricsRef,
  zoomLevel,
  zoom,
}: MapEffectsProps) => {
  useMapZoom(mapRef, zoom)
  useWaterSystemMetrics(dispatch, zoomLevel, waterSystemMapMetrics, waterSystemMapMetricsRef, includeTransient)
  useIncludeTransientRefetch(dispatch, zoomLevel, selectedState, countyViolations, selectedCounty, includeTransient)
  useMapStyles(
    mapRef,
    zoomLevel,
    regionsViolations,
    countyViolations,
    refs,
    prevSelectedCode,
    boundingBox,
    selectedAddress,
    dispatch,
  )
  useMapMarkers(mapRef, coordinates, refs, theme, dispatch, selectedAddress, isMobile)
  useMapResize(mapRef)
  useMapNavigationTranisitions(
    mapRef,
    selectedState,
    selectedStateRef,
    mapCenter,
    mapCenterRef,
    selectedAddress,
    selectedAddressRef,
    states,
    statesRef,
    selectedCounty,
    dispatch,
    zoom,
  )
}
